/** A pin as the map knows it, before anything is projected. */
export interface GeoPin { id: string; lat: number; lng: number }

/** A pin as the screen knows it: centre in pixels, any origin will do. */
export interface PinPoint { id: string; x: number; y: number }

/** How far a pin is drawn from the point it belongs to, in pixels. */
export interface PinOffset { dx: number; dy: number }

interface Options {
  /** Closer than this and two pin centres read as one pin. */
  spacing?: number;
  /** How many pins fit on the innermost ring before a second one opens. */
  ringCapacity?: number;
  /** How far the outermost ring may sit from the group's centre. */
  maxRadius?: number;
}

const DEFAULT_SPACING = 26;   // 24px pin + 2px of air
const DEFAULT_CAPACITY = 8;
/**
 * Two rings' worth: 34px for a full inner ring, plus a ring gap. Past that the
 * fan stops being a fan.
 *
 * Zoomed far out, `2 ** zoom` collapses a whole trip into a handful of pixels,
 * so every pin lands in one group: 40 pins fan onto 5 rings up to 138px out, and
 * the caller draws a leader line from each pin's anchor to where it moved. Forty
 * lines radiating from one point is the "todos los pines con estrella" the user
 * reported — the fan-out working exactly as written, at a scale where the answer
 * is nonsense. Overlapping pins you can zoom into beat a starburst you cannot.
 */
const DEFAULT_MAX_RADIUS = 60;

function distance(a: PinPoint, b: PinPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Rounded so the same layout always produces the same CSS. */
function round(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * The radius of every ring a group of `total` pins needs.
 *
 * A ring is as small as it can be and still be readable: the radius at which its
 * pins sit exactly `spacing` apart. A second ring then keeps a full `spacing` of
 * clearance from the one inside it — the radial gap is a lower bound on the
 * distance between any two pins on concentric rings, so that is all it takes.
 */
function ringRadii(total: number, capacity: number, spacing: number): number[] {
  const radii: number[] = [];
  for (let ring = 0; ring * capacity < total; ring++) {
    const count = Math.min(total - ring * capacity, capacity);
    const own = count < 2 ? 0 : spacing / (2 * Math.sin(Math.PI / count));
    radii.push(ring === 0 ? own : Math.max(own, radii[ring - 1] + spacing));
  }
  return radii;
}

/**
 * Overlapping pins, grouped by leader scan: the first unclaimed pin in a
 * position-ordered sweep claims every unclaimed pin within `spacing` of *it*.
 *
 * Deliberately not single-linkage. A chain of pins each 20px from the next is a
 * readable trail, and transitive grouping would sweep the whole chain into one
 * fan — moving pins that were never hidden to fix two that were.
 */
function groupOverlapping(points: PinPoint[], spacing: number): PinPoint[][] {
  const order = points
    .map((_, i) => i)
    .sort((a, b) => {
      const pa = points[a], pb = points[b];
      return pa.x - pb.x || pa.y - pb.y || pa.id.localeCompare(pb.id);
    });

  const claimed = new Set<number>();
  const groups: PinPoint[][] = [];

  for (const leader of order) {
    if (claimed.has(leader)) continue;
    claimed.add(leader);
    const members = [leader];
    for (const other of order) {
      if (claimed.has(other)) continue;
      if (distance(points[leader], points[other]) >= spacing) continue;
      claimed.add(other);
      members.push(other);
    }
    if (members.length > 1) {
      // Back to input order: for route stops that is the stop order, so the fan
      // reads 1, 2, 3 clockwise from the top instead of an arbitrary shuffle.
      groups.push(members.sort((a, b) => a - b).map(i => points[i]));
    }
  }

  return groups;
}

/**
 * Where each pin has to be drawn so that none of them hides another.
 *
 * Only pins that collide appear in the result; everything else is drawn where it
 * belongs and is absent from the map. A displaced pin is still anchored to its
 * real point by the caller's leader line — the offset is a drawing instruction,
 * never a new position for the place.
 */
export function fanOutOverlaps(
  points: PinPoint[],
  {
    spacing = DEFAULT_SPACING,
    ringCapacity = DEFAULT_CAPACITY,
    maxRadius = DEFAULT_MAX_RADIUS,
  }: Options = {},
): Map<string, PinOffset> {
  const offsets = new Map<string, PinOffset>();
  if (points.length < 2) return offsets;

  for (const group of groupOverlapping(points, spacing)) {
    const cx = group.reduce((sum, p) => sum + p.x, 0) / group.length;
    const cy = group.reduce((sum, p) => sum + p.y, 0) / group.length;

    const radii = ringRadii(group.length, ringCapacity, spacing);
    // A group that would need more room than this is not hiding a few pins from
    // each other — it is the whole map collapsed into one spot. Leave it drawn
    // as it was before the fan existed; zooming in breaks it up on its own.
    if (radii[radii.length - 1] > maxRadius) continue;

    group.forEach((pin, i) => {
      const ring = Math.floor(i / ringCapacity);
      const slot = i % ringCapacity;
      const inRing = Math.min(group.length - ring * ringCapacity, ringCapacity);
      const radius = radii[ring];
      const angle = -Math.PI / 2 + (2 * Math.PI * slot) / inRing;

      offsets.set(pin.id, {
        dx: round(cx + radius * Math.cos(angle) - pin.x),
        dy: round(cy + radius * Math.sin(angle) - pin.y),
      });
    });
  }

  return offsets;
}
